import PatientUtils from './PatientUtils';
import moment from 'moment-timezone';
import MiscUtils from './MiscUtils';
import _ from 'lodash';
import StaffUtils from 'services/StaffUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import he from 'he';
import textVersion from 'textversionjs';
import i18n from './i18n';
import { using } from './TypeUtils';
import { Patient, Admin, Physician } from 'generated/api';
import { CAREY_DISPLAY_NAME, isCareyMessage } from 'constants/carey';

interface ChatMessage {
  id?: string;
  author?: string;
  from?: string;
  text?: string;
  body?: string;
  message?: string;
  time: number;
  date?: number;
  role?: string;
  content?: string;
  sections?: any;
  context?: any;
  type?: string;
  media?: any;
  attributes?: any;
  sid?: string;
  lastRead?: boolean;
  dateCreated?: string;
  media_type?: string;
  [key: string]: any;
}

interface GroupedMessage {
  staff: boolean | undefined;
  author: string | undefined;
  name: string | undefined;
  time: any;
  messages: any[];
  own: boolean;
}

interface ChannelAttributes {
  patient_recipient_name?: string;
  [key: string]: any;
}

interface ChatArchive {
  id: string;
  date_end?: number;
  messages?: ChatMessage[];
  attributes?: ChannelAttributes;
  [key: string]: any;
}

class ChatUtils {
  extractPatientIdFromMessage(text: string = ''): string | undefined {
    return /patientId\((.*)\)/.exec(text)?.[1];
  }

  cleanMessageContent(content: string = '', showOriginal: boolean = false): string {
    const NEWLINE_TOKEN = '__NEWLINE__';
    let result: string[] = [];
    content = content.trim();

    content.split('<hr />').forEach((split) => {
      if (result.length === 0 || showOriginal) {
        split = split.split('\r\n').join(NEWLINE_TOKEN);
        split = split.split('\r').join(NEWLINE_TOKEN);
        split = split.split('\n').join(NEWLINE_TOKEN);

        let cutQuote = false;
        if (showOriginal) {
          split = split.split('<blockquote>').join('__BLOCKQUOTE__');
          split = split.split('</blockquote>').join('__/BLOCKQUOTE__');
        } else {
          // The quoted reply chain is always wrapped in <blockquote> tags, so
          // cut it off there. Relying on a "... wrote:" footer misses replies
          // whose attribution line is localized (e.g. Korean "님이 작성:").
          const blockquoteIndex = split.indexOf('<blockquote');
          if (blockquoteIndex !== -1) {
            split = split.slice(0, blockquoteIndex);
            cutQuote = true;
          }
        }

        let workingVersion = he.decode(
          textVersion(split, {
            keepNbsps: true,
            linkProcess: (href: string, linkText: string) => `${linkText} [ ${href} ]`,
          }).trim(),
        );

        workingVersion = workingVersion.split(NEWLINE_TOKEN).join('\n');

        let lines = workingVersion.split('\n');
        let shouldStop = false;
        let newResult: string[] = [];
        let lastLineBlank = false;
        lines.forEach((line: string) => {
          shouldStop = shouldStop || line.endsWith('@careand.ca> wrote:');

          if (showOriginal) shouldStop = false;

          if (!shouldStop) {
            const isBlank = line.trim() === '';
            if (isBlank && lastLineBlank) return;
            lastLineBlank = isBlank;
            newResult.push(line);
          }
        });

        if (cutQuote) {
          // Drop the dangling attribution line that introduced the quote we
          // just removed (e.g. "On <date> <name> wrote:" / "... 님이 작성:").
          while (newResult.length && (newResult[newResult.length - 1] ?? '').trim() === '') {
            newResult.pop();
          }
          if (newResult.length && (newResult[newResult.length - 1] ?? '').trim().endsWith(':')) {
            newResult.pop();
          }
        }

        result.push(newResult.join('\n'));
      }
    });

    // Simplified view only ever keeps the first <hr />-separated segment.
    if (!showOriginal) return result.join('\n').trim();

    // Full view: besides <blockquote>-based quoting, replies are also threaded
    // with <hr /> separators (Outlook-style "From:/Sent:/Subject:" headers and
    // the app's own "From: <sender> — <date>" headers, with no <blockquote>).
    // Each successive <hr /> segment is a deeper quote level, so wrap them in
    // blockquote markers to render as nested gray quotes like everything else.
    const segments = result.map((segment) => segment.trim()).filter(Boolean);
    if (segments.length === 0) return '';
    let assembled = segments[0] ?? '';
    for (let i = 1; i < segments.length; i++) {
      assembled += `\n__BLOCKQUOTE__\n${segments[i]}`;
    }
    assembled += '\n__/BLOCKQUOTE__'.repeat(segments.length - 1);
    return assembled.trim();
  }

  getAssistantGroupedMessages(messages: ChatMessage[], ownUser: any): GroupedMessage[] {
    let result: GroupedMessage[] = [];

    messages.forEach((message) => {
      const own = message.role === 'user';
      result.push({
        staff: undefined,
        author: undefined,
        name: own ? StaffUtils.getName(ownUser) : 'Carey (AI Assistant)',
        time: message.date ?? message.time,
        messages: [
          {
            text: message.content,
            time: message.date ?? message.time,
            sections: message.sections,
            context: message.context,
            markdown: true,
          },
        ],
        own,
      });
    });
    return result;
  }

  getGroupedMessages(
    messages: ChatMessage[] | undefined,
    ownUserId: string,
    patient: Patient,
    channelAttributes: ChannelAttributes = {},
    lastRead: number | undefined,
    admins: Record<string, Admin> = {},
    physicians: Record<string, Physician> = {},
  ): GroupedMessage[] {
    let groupedMessages: GroupedMessage[] = [];

    if (messages !== undefined && messages.length > 0) {
      const withId = messages.filter((m) => m.id);
      const withoutId = messages.filter((m) => !m.id);
      if (withId.length > 0) {
        messages = _.sortBy([..._.uniqBy(withId, 'id'), ...withoutId], ['time']);
      }

      for (let index in messages) {
        let message = PerformanceUtils.clone(messages[index]!);

        message.lastRead = !!(lastRead && index === '' + lastRead);

        let own = this.isOwn(message.author!, ownUserId);
        let author = message.author;

        let name: string | undefined = own ? 'Me' : undefined;
        let staff = false;

        if (!own && author === patient.id) {
          name = PatientUtils.getName(patient);
        }

        if (!name) {
          staff = true;
          let user =
            using(admins[message.author!], (user) => ({ user, role: 'admin' })) ??
            using(physicians[message.author!], (user) => ({ user, role: 'physician' }));

          name = !!user
            ? StaffUtils.getName(user.user) + (user.role === 'admin' ? ` (${i18n.concierge})` : '')
            : channelAttributes?.patient_recipient_name;
        }

        let afterFiveMinutes = false;

        if (parseInt(index) > 0) {
          let prevTimePlusFive = moment(messages[parseInt(index) - 1]!.time).add(5, 'minutes');
          let currentTime = moment.unix(message.time);
          afterFiveMinutes = prevTimePlusFive.isBefore(currentTime);
        }

        if (
          groupedMessages.length === 0 ||
          groupedMessages[groupedMessages.length - 1]!.author !== author ||
          afterFiveMinutes
        ) {
          groupedMessages.push({
            staff,
            author,
            name,
            time: moment(message.time),
            messages: [message],
            own,
          });
        } else {
          groupedMessages[groupedMessages.length - 1]!.messages.push(message);
        }
      }
    }

    return groupedMessages;
  }

  getMatchingArchive(roomName: string, archives: ChatArchive[] = []): ChatArchive | undefined {
    return archives.find((archive) => {
      return !archive.date_end && archive.id === roomName;
    });
  }

  getMergedMessages(
    roomName: string,
    channelMessages: ChatMessage[],
    archives: ChatArchive[] = [],
  ): ChatMessage[] {
    let matchingArchive = archives.find((archive) => {
      return !archive.date_end && archive.id === roomName;
    });

    let messagesByDate: Record<number, ChatMessage[]> = {};
    let archivedMessages = matchingArchive?.messages || [];

    function addToMap(message: ChatMessage): void {
      let time = message.time;
      let text = message.text || message.message;
      messagesByDate[time] = messagesByDate[time] || [];
      let alreadyIncluded = messagesByDate[time]!.find(
        (m) => m.text === text || m.message === text,
      );
      if (!alreadyIncluded) {
        messagesByDate[time]!.push({ ...message, time });
        return;
      }
      // The same logical message can arrive from two sources — the live chat channel and the REST
      // archive — and they aren't always equally complete. Notably, a Carey handoff artifact's
      // `staff_summary` is present on the archived copy but omitted from the realtime echo. Dedup
      // used to keep whichever copy was added first (the channel copy) and drop the other, silently
      // losing those fields. Merge the attributes instead so artifact data present in either copy
      // survives.
      if (alreadyIncluded.attributes || message.attributes) {
        alreadyIncluded.attributes = {
          ...alreadyIncluded.attributes,
          ...message.attributes,
          artifact_payload: {
            ...alreadyIncluded.attributes?.artifact_payload,
            ...message.attributes?.artifact_payload,
          },
        };
      }
    }

    channelMessages.forEach(addToMap);
    archivedMessages.forEach(addToMap);

    return _.flatten(Object.values(messagesByDate));
  }

  getChatGroupedMessages(
    messages: ChatMessage[] | undefined,
    ownUserId: string,
    patient: Patient | undefined,
    admins: Record<string, Admin> = {},
    physicians: Record<string, Physician> = {},
  ): GroupedMessage[] {
    let groupedMessages: GroupedMessage[] = [];

    if (messages !== undefined && messages.length > 0) {
      const withId = messages.filter((m) => m.id);
      const withoutId = messages.filter((m) => !m.id);
      if (withId.length > 0) {
        messages = _.sortBy([..._.uniqBy(withId, 'id'), ...withoutId], ['time']);
      }

      for (let index in messages) {
        let message = messages[index]!;
        //const {from, text, id, to, by, date, room_name} = message;

        let authorId = message.author || message.from!.split('/')[1] || 'no-id';

        let own = this.isOwn(authorId!, ownUserId);

        let name: string | undefined = own ? 'Me' : undefined;
        let staff = false;

        // Carey (patient AI assistant) messages are tagged in attributes rather than resolved via
        // the admins/physicians maps — render them distinctly with a fixed display name.
        if (!own && isCareyMessage(message)) {
          name = CAREY_DISPLAY_NAME;
          staff = true;
        }

        if (!own && !name && authorId === patient?.id) {
          name = PatientUtils.getName(patient);
        }

        if (!name) {
          staff = true;
          let user =
            using(admins[authorId!], (user) => ({ user, role: 'admin' })) ||
            using(physicians[authorId!], (user) => ({ user, role: 'physician' }));
          name = !!user?.user
            ? StaffUtils.getName(user.user) +
              ` (${user.role === 'admin' ? i18n.concierge : 'Staff'})`
            : undefined;
        }

        let afterFiveMinutes = false;

        if (parseInt(index) > 0) {
          let date = moment.unix(message.time);
          let prevDate = moment.unix(messages[parseInt(index) - 1]!.time);
          let prevTimePlusFive = prevDate.add(5, 'minutes');
          afterFiveMinutes = prevTimePlusFive.isBefore(date);
        }

        if (
          groupedMessages.length === 0 ||
          groupedMessages[groupedMessages.length - 1]!.author !== authorId ||
          afterFiveMinutes
        ) {
          groupedMessages.push({
            staff,
            author: authorId,
            name,
            time: message.time,
            messages: [message],
            own,
          });
        } else {
          groupedMessages[groupedMessages.length - 1]!.messages.push(message);
        }
      }
    }

    return groupedMessages;
  }

  getNameFromArchive(archive: ChatArchive): string {
    return archive.attributes?.patient_recipient_name || '';
  }

  isOwn(author: string, ownUserId: string): boolean {
    return author.indexOf(ownUserId) !== -1;
  }

  async getChatMessageObject(message: any): Promise<any> {
    let result: any = {
      author: message.author,
      message: message.body,
      time: moment(message.dateCreated).unix(),
      attributes: message.attributes,
      id: message.sid,
    };

    if (message.type === 'media') {
      let url = await message.media.getContentTemporaryUrl();
      result.media = await MiscUtils.getLocalUrlFromUrl(url);
      result.media_type = message.media.contentType;
    }
    return result;
  }

  getChatDescriptionFromBubble(bubble: any = {}): string {
    let description = '';

    if (bubble.helpdesk) return 'Helpdesk Email';

    switch (bubble.type) {
      case 'on-demand':
        description = 'On-demand chat';
        break;
      case 'appointment':
        description =
          (bubble.appointment_type !== undefined ? bubble.appointment_type + ' ' : '') +
          'Appointment';
        break;
      case 'general':
        description = 'General chat';
        break;
      case 'schedule-appointment':
        description = 'Practitioner Assignment';
        break;
      case 'booking':
        description = 'Appointment Booking';
        break;
      default:
        description = bubble.type;
    }

    return description;
  }
}

export default new ChatUtils();
